/**
 * The setup wizard (docs/PLAN-onboarding.md, design: docs/DESIGN.md artboards 1–5) over the live demo island.
 *
 *   new user   Welcome → [Where you play] → [Your blocks] → Your world → Opening (until the world is drawn) → fly-in → tour
 *   returning  Home ("Continue ‹last world›" + all worlds) — or Your blocks when no jar is cached → Opening → the app
 *   guest      Invite card (textures inline) → Join
 *
 * [ ] = skipped when the desktop app finds Minecraft by itself. Nothing here needs an account or a server.
 *
 * Layouts: the welcome screen centres its copy over the full-window 3D backdrop (`hero`); the steps before the world
 * (where you play, your blocks) and the account invite are one centred column on the shell, no frame (`center`); the
 * world step / Home and the guest invite are a 560 px pane plus a framed window (`split`: the frame's outside is painted
 * in the shell colour, so it reads like a content card); opening a world keeps the 3D view full-window with type along
 * the bottom (`opening`). The island only draws where it can be seen (welcome, guest invite, opening).
 */
import {
  ArrowLeft, ArrowRight, Box, Check, ChevronRight, Flame, Folder, Info, Link2, LoaderCircle, Lock, Minus,
  Package, Puzzle, ShieldCheck, Triangle, TriangleAlert, Upload, Users, Wrench,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { controller, type RecentWorld } from "../../app/controller.ts";
import { useApp } from "../../state/store.ts";
import { fileUrl, findMinecraft, isDesktop, type FoundMinecraft, type FoundWorld } from "../../lib/desktop.ts";
import { EULA_URL, findClient, fmtMB, mojangAccepted, type MojangClient } from "../../lib/mojang.ts";
import { classifyAll, droppedFolder, looksLikeFolder } from "../../lib/dropped.ts";
import { DEMO_NAME } from "../../lib/demoWorld.ts";
import { demoIslandPicture, namePicture } from "../../lib/worldThumb.ts";
import { detectOS, jarHelp, LAUNCHERS, openFolderSteps, OS_NAME, savesHelp, zipHelp, type Launcher, type OS } from "../../lib/launchers.ts";
import { fmtCount, isNewUser, prefersReducedMotion, useOnboarding, type WizardStep } from "../../lib/onboarding.ts";
import { AccountButton } from "../Account.tsx";
import { AccountInviteSection, SharedWorldsList } from "../Share.tsx";
import { LegalLink } from "../Legal.tsx";
import { BrandMark, WorldTile } from "../Brand.tsx";
import { BlockStrip, DesktopWorlds, DropRow, DropZone, fmtDate, fmtSize, JoinByLink, JoinSection, PathField, WorldRow } from "./parts.tsx";
import { AcademyLink, type AcademyKey } from "../Academy.tsx";
import "./onboarding.css";

// ---------------- actions ----------------

const ob = () => useOnboarding.getState();
const go = (step: WizardStep) => ob().set({ step, note: null });
const assetsReady = () => { const s = useApp.getState(); return !!s.assetsLabel && !s.mapColoursOnly; };

/** Open a world from the wizard. New users keep the loading screen until the world is drawn, then fly in (+ tour). */
async function startWorld(open: () => Promise<unknown>, meta?: { name: string; profile?: string }) {
  const nu = isNewUser();
  ob().set({ holding: nu, landing: nu, note: null, pendingWorld: null, opening: meta ?? null, leaving: false });
  await open();
  if (useApp.getState().phase !== "ready") ob().set({ holding: false, landing: false, opening: null });
}

function openDemo() {
  return startWorld(() => controller.openDemo(), { name: DEMO_NAME });
}

/** where "Back" on the loading screen returns to: the screen the world was opened from */
let backTo: WizardStep | null = null;

/** Esc on the loading screen: back to the world list (once the world is on screen, if it's still being read). */
function backToWorlds() {
  backTo = ob().step;
  if (useApp.getState().phase === "ready") {
    ob().set({ holding: false, landing: false, opening: null, leaving: false });
    controller.closeWorld();
  } else ob().set({ leaving: true, landing: false });
}

function loadJar(jar: File, extras: File[]) {
  return controller.loadAssets([...extras.map((f) => ({ label: f.name, blob: f as Blob })), { label: jar.name.replace(/\.jar$/i, ""), blob: jar }]);
}

/**
 * Smart drops: whatever is dropped or chosen, send it where it belongs (or say kindly what it is).
 * @param dt the drop itself (lets us read a dropped world folder)
 */
async function routeFiles(files: File[], dt?: DataTransfer) {
  // dropping files counts as getting started
  ob().set({ note: null, ...(ob().step === "welcome" || ob().step === "launcher" ? { welcomed: true } : {}) });
  if (dt) {
    const folder = await droppedFolder(dt).catch(() => null);
    if (folder) {
      if (!folder.files.some((f) => /(^|\/)level\.dat$/.test(f.path))) {
        ob().set({ note: { tone: "warn", text: `${folder.name} isn't a world folder (there's no level.dat inside). Pick the world's own folder inside saves.` } });
        return;
      }
      if (assetsReady()) void startWorld(() => controller.openFolderFiles(folder.files, folder.name), { name: folder.name });
      else {
        go("textures");
        ob().set({ pendingWorld: { name: folder.name, files: folder.files }, note: { tone: "info", text: `Got your world ${folder.name}. It opens right after the textures. Now the game jar:` } });
      }
      return;
    }
    files = Array.from(dt.files);
  }
  if (!files.length) return;
  const c = await classifyAll(files);
  const game = c.find((x) => x.kind === "game-jar");
  const world = c.find((x) => x.kind === "world-zip");
  const extras = c.filter((x) => x.kind === "mod-jar" || x.kind === "pack").map((x) => x.file);
  if (game) {
    if (useApp.getState().phase !== "setup") return;
    if (ob().step !== "textures") go("textures");
    if (world) ob().set({ pendingWorld: { name: world.file.name, zip: world.file } });
    const all = [...ob().extras, ...extras];
    ob().set({ extras: [] });
    await loadJar(game.file, all);
    return;
  }
  if (world) {
    if (assetsReady()) { void startWorld(() => controller.openZip(world.file), { name: world.file.name.replace(/\.zip$/i, "") }); return; }
    go("textures");
    ob().set({ pendingWorld: { name: world.file.name, zip: world.file }, note: { tone: "info", text: `That's your world (${world.file.name}). It opens right after the textures, so now the game jar:` } });
    return;
  }
  if (extras.length) {
    ob().set({ extras: [...ob().extras, ...extras] });
    const mod = c.find((x) => x.kind === "mod-jar");
    ob().set({ note: { tone: "info", text: mod
      ? `${mod.file.name} is a mod, kept for its textures. Now drop the game jar itself (e.g. 26.2.jar).`
      : `Resource pack added (${extras.map((f) => f.name).join(", ")}). It's layered on top. Now the game jar itself.` } });
    return;
  }
  const first = c[0];
  const text = first.kind === "plan" ? "That's a plan file (.mcplan). Open your world first, then load it with File, then Load."
    : first.kind === "schematic" ? "That's a schematic. Open your world first, then drop it on the map (or press 7)."
    : first.kind === "folder" || looksLikeFolder(first.file) ? "Drop the world folder itself onto this window, or zip it first."
    : `MC Planner can't read ${first.file.name}. It needs the game's .jar and a world (.zip or its folder).`;
  ob().set({ note: { tone: "warn", text } });
}

function afterWelcome() {
  ob().set({ welcomed: true });
  if (assetsReady()) go(isNewUser() ? "world" : "home");
  else if (!isDesktop() && !ob().launcher) go("launcher");
  else go("textures");
}

/** the world is drawn and the loading screen is gone: fly in, say hello, start the tour */
function land() {
  const reduce = prefersReducedMotion();
  if (!reduce) controller.engine?.flyIn(2600);
  const name = useApp.getState().level?.name ?? "your world";
  const text = `Welcome to ${name}. Press H to fly back to this spot any time.`;
  useApp.getState().set({ notice: { text } });
  setTimeout(() => { if (useApp.getState().notice?.text === text) useApp.getState().set({ notice: null }); }, 7000);
  setTimeout(() => {
    const s = ob();
    if (!s.tourDone && useApp.getState().phase === "ready" && s.tourStep === null) s.set({ tourStep: 0 });
  }, reduce ? 400 : 2700);
}

// ---------------- the backdrop: where the island sits ----------------

/** current horizontal shift of the 3D view (fraction of its width, positive = to the right) */
let viewShift = 0;
function applyShift(f: number) {
  viewShift = f;
  const cam = controller.engine?.camera;
  if (!cam) return;
  // a view offset moves the picture without resizing the canvas (fractions of the view, so it survives resizes)
  if (Math.abs(f) < 1e-4) cam.clearViewOffset();
  else cam.setViewOffset(1000, 1000, -f * 1000, 0, 1000, 1000);
}

/** Slide the demo island to the right of the welcome copy / into the middle of the preview frame. */
function useViewShift(target: number, instant: boolean) {
  useEffect(() => {
    const from = viewShift;
    if (Math.abs(from - target) < 1e-4) return;
    const ms = prefersReducedMotion() || instant ? 0 : 700;
    const t0 = performance.now();
    let raf = 0;
    const step = () => {
      const k = ms ? Math.min(1, (performance.now() - t0) / ms) : 1;
      applyShift(from + (target - from) * (1 - Math.pow(1 - k, 3)));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    step();
    return () => cancelAnimationFrame(raf);
  }, [target, instant]);
  useEffect(() => () => applyShift(0), []);
}

function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return w;
}

/** the left pane's width (CSS: clamp(400px, 46vw, 560px); one column below 720 px) */
const paneWidth = (w: number) => (w < 720 ? w : Math.max(400, Math.min(560, w * 0.46)));

// ---------------- the wizard ----------------

type Screen = "hero" | "split" | "center" | "opening" | "none";

export function Wizard() {
  const phase = useApp((s) => s.phase);
  const invite = useApp((s) => s.invite);
  const accountInvite = useApp((s) => s.accountInvite);
  const step = useOnboarding((s) => s.step);
  const holding = useOnboarding((s) => s.holding);
  const landing = useOnboarding((s) => s.landing);
  const leaving = useOnboarding((s) => s.leaving);
  const open = phase !== "ready" || holding || leaving;
  const [shown, setShown] = useState(open);
  const [found, setFound] = useState<FoundMinecraft | null>(null);
  const [glass, setGlass] = useState(false);
  const width = useWindowWidth();
  const desktop = isDesktop();
  const rescan = () => { if (desktop) void findMinecraft().then(setFound).catch((e) => { console.warn(e); setFound(null); }); };

  // first step: welcome for new users, Home when a jar is cached, else the textures step
  useEffect(() => {
    let alive = true;
    rescan();
    void controller.hasCachedAssets().then((cached) => {
      if (!alive || ob().step !== "boot") return;
      go(!ob().welcomed ? "welcome" : cached ? "home" : "textures");
    });
    return () => { alive = false; };
  }, []);

  // back on setup (world closed): Home for returning users, the world step for new ones
  const prevPhase = useRef(phase);
  useEffect(() => {
    if (prevPhase.current !== "setup" && phase === "setup" && ob().step !== "boot") {
      if (backTo && backTo !== "boot") go(backTo);
      else if (!assetsReady() && !useApp.getState().progress) go("textures");
      else go(isNewUser() ? "world" : "home");
    }
    if (phase === "setup") backTo = null;
    prevPhase.current = phase;
    if (phase === "setup") void controller.showBackdrop();
  }, [phase]);

  // world drawn, loading screen gone → fly-in + tour
  useEffect(() => {
    if (phase === "ready" && !holding && landing && !leaving) {
      ob().set({ landing: false, opening: null });
      land();
    }
  }, [phase, holding, landing, leaving]);

  // "Back to worlds" while the world was still being read: close it as soon as it's there
  useEffect(() => {
    if (!leaving) return;
    if (phase === "ready") { ob().set({ leaving: false, holding: false, landing: false, opening: null }); controller.closeWorld(); }
    else if (phase === "setup") ob().set({ leaving: false, holding: false, landing: false, opening: null });
  }, [leaving, phase]);

  // frosted glass once we know the machine draws the backdrop smoothly
  useEffect(() => {
    if (glass) return;
    const t = setInterval(() => { if (controller.engine?.backdropFast) setGlass(true); }, 400);
    return () => clearInterval(t);
  }, [glass]);

  // fade out instead of vanishing
  useEffect(() => {
    if (open) { setShown(true); return; }
    const t = setTimeout(() => setShown(false), prefersReducedMotion() ? 0 : 480);
    return () => clearTimeout(t);
  }, [open]);

  // while the wizard is open the 3D view fills the window (the app's card layout comes back with the app)
  useEffect(() => {
    document.documentElement.classList.toggle("wz-on", open);
    return () => document.documentElement.classList.remove("wz-on");
  }, [open]);

  let screen: Screen = "none";
  let content: React.ReactNode = null;
  if (holding || phase === "loading" || leaving) { screen = "opening"; content = <Opening hold={holding} />; }
  else if (invite) { screen = "split"; content = <InviteStep found={found} />; }
  else if (accountInvite) { screen = "center"; content = <AccountInvite />; }
  else if (step === "welcome") { screen = "hero"; content = <Welcome />; }
  else if (step === "launcher") { screen = "center"; content = <LauncherStep />; }
  else if (step === "textures") { screen = "center"; content = <TexturesStep found={found} />; }
  else if (step === "world" || step === "home") { screen = "split"; content = <WorldStep home={step === "home"} found={found} onRescan={rescan} />; }
  // boot: a few ms

  // the island: centred behind the welcome copy, centred in the guest's preview frame, centred while a world opens
  const shift = phase === "setup" && open && screen === "split" && width >= 720 ? paneWidth(width) / (2 * width) : 0;
  // a world opening (or open) is never shifted: it's centred at once
  useViewShift(shift, phase !== "setup");
  // nothing shows the island on the centred steps and the world step (its frame shows the world's picture): it rests
  const hidden = phase === "setup" && open && (screen === "center" || (screen === "split" && !invite && (step === "world" || step === "home")));
  useEffect(() => {
    controller.engine?.setPaused(hidden);
  }, [hidden]);
  useEffect(() => () => controller.engine?.setPaused(false), []);

  // while fading out, keep showing what was there
  const last = useRef<{ screen: Screen; content: React.ReactNode }>({ screen: "none", content: null });
  if (open) last.current = { screen, content };
  else ({ screen, content } = last.current);
  if (!shown) return null;

  return (
    <div
      className={`wizard wz-${screen}${open ? "" : " closing"}${phase === "ready" ? " over-app" : ""}${glass ? " wz-frosted" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="wz-title"
      onDragOver={(e) => { if (e.dataTransfer.types.includes("Files")) e.preventDefault(); }}
      onDrop={(e) => { if (!e.dataTransfer.types.includes("Files")) return; e.preventDefault(); if (phase === "setup" && !invite && !accountInvite) void routeFiles([], e.dataTransfer); }}
    >
      {content}
    </div>
  );
}

// ---------------- pieces shared by the screens ----------------

function Brand({ by }: { by?: boolean }) {
  return <span className="brand wz-brand"><BrandMark size={22} />MC Planner{by && <span className="wz-by">by Lab43</span>}</span>;
}

/** Academy + Sign in (optional — only needed to plan together) */
function TopNav() {
  return (
    <nav className="wz-nav" aria-label="MC Planner">
      <AcademyLink to="home" className="wz-navlink">Academy</AcademyLink>
      <div className="brand-account"><AccountButton compact /></div>
    </nav>
  );
}

function Note() {
  const note = useOnboarding((s) => s.note);
  if (!note) return null;
  return (
    <p className={`wz-note ${note.tone}`} role="status">
      {note.tone === "warn" ? <TriangleAlert size={16} aria-hidden="true" /> : <Info size={16} aria-hidden="true" />}
      <span>{note.text}</span>
    </p>
  );
}

type FlowStep = "launcher" | "textures" | "world";
const flowFor = (): FlowStep[] => (isDesktop() ? ["textures", "world"] : ["launcher", "textures", "world"]);

function Stepper({ at }: { at: FlowStep }) {
  const launcher = useOnboarding((s) => s.launcher);
  const assetsLabel = useApp((s) => s.assetsLabel);
  const mapColoursOnly = useApp((s) => s.mapColoursOnly);
  const flow = flowFor();
  const i = flow.indexOf(at);
  const ready = !!assetsLabel && !mapColoursOnly;
  const label: Record<FlowStep, string> = { launcher: "Where you play", textures: "Your blocks", world: "Your world" };
  const doneLabel = (f: FlowStep) =>
    f === "launcher" ? (LAUNCHERS.find((l) => l.id === launcher)?.name ?? label.launcher)
    : f === "textures" ? (ready ? `${fmtCount(controller.blockCount())} blocks` : "Skipped")
    : label.world;
  return (
    <ol className="wz-stepper" aria-label="Setup steps" style={{ "--n": flow.length } as React.CSSProperties}>
      {flow.map((f, k) => {
        const state = k < i ? (f === "textures" && !ready ? "skipped" : "done") : k === i ? "now" : "todo";
        const inner = (
          <>
            <span className="wz-bar" aria-hidden="true" />
            <span className={`lbl${state === "done" && f === "textures" ? " ok" : ""}`}>
              {state === "done" && <Check size={12} strokeWidth={3} aria-hidden="true" />}
              {state === "skipped" && <Minus size={12} strokeWidth={3} aria-hidden="true" />}
              {state === "done" || state === "skipped" ? doneLabel(f) : label[f]}
            </span>
          </>
        );
        return (
          <li key={f} className={state} aria-current={state === "now" ? "step" : undefined}>
            {k < i ? <button type="button" onClick={() => go(f)} title={`Back to ${label[f].toLowerCase()}`}>{inner}</button> : <span className="seg-step">{inner}</span>}
          </li>
        );
      })}
    </ol>
  );
}

/** Enter = the step's primary action, Esc = back, and optional digit keys — unless typing or a dialog has focus. */
function useStepKeys(keys: { enter?: () => void; back?: () => void; digit?: (n: number) => void }) {
  const ref = useRef(keys);
  ref.current = keys;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      const t = e.target as HTMLElement | null;
      const inWizard = !t || t === document.body || !!t.closest?.(".wizard");
      if (!inWizard || t?.closest?.("input, textarea, select, [contenteditable]")) return;
      const k = ref.current;
      if (e.key === "Enter" && k.enter && !t?.closest?.("button, a, summary, [role=button]")) { e.preventDefault(); k.enter(); }
      else if (e.key === "Escape" && k.back) { e.preventDefault(); k.back(); }
      else if (k.digit && /^[1-9]$/.test(e.key)) { e.preventDefault(); k.digit(Number(e.key)); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}

/** ↑ ↓ move between the launcher tiles / the worlds (Enter opens, as on any button) */
function arrowKeys(e: React.KeyboardEvent<HTMLElement>) {
  if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
  const t = e.target as HTMLElement;
  const group = t.closest(".wz-tile") ? ".wz-tile" : t.closest(".wz-world, .wz-continue") ? ".wz-world, .wz-continue" : t.matches("h1") ? ".wz-tile, .wz-world, .wz-continue" : null;
  if (!group) return;
  const items = [...e.currentTarget.querySelectorAll<HTMLButtonElement>(group)].filter((b) => !b.disabled);
  const i = items.indexOf(t.closest(group) as HTMLButtonElement);
  const next = i < 0 ? items[0] : items[i + (e.key === "ArrowDown" ? 1 : -1)];
  if (next) { e.preventDefault(); next.focus(); }
}

/** A step: the 560 px pane on the shell (brand, stepper, title, content, Back / Continue) + the framed preview. */
/**
 * A step: the pane (brand, stepper, title, content, Back / Continue) and, in the `split` layout, the framed preview
 * (`stage`). Centred steps (`centered`) are the pane alone, in the middle of the window.
 */
function Split({ at, head, title, sub, more, children, stage, back, primary, footLeft, className, centered }: {
  at?: FlowStep; head?: React.ReactNode; title: React.ReactNode; sub?: React.ReactNode; more?: [AcademyKey, string];
  children?: React.ReactNode; stage?: React.ReactNode; back?: () => void; primary?: React.ReactNode; footLeft?: React.ReactNode; className?: string;
  centered?: boolean;
}) {
  const flow = flowFor();
  const i = at ? flow.indexOf(at) : -1;
  const step = useOnboarding((s) => s.step);
  // each step starts with the focus on its heading (screen readers hear it; Enter, digits and arrows work from there)
  const h1 = useRef<HTMLHeadingElement>(null);
  useEffect(() => { if (!document.activeElement || document.activeElement === document.body || !document.activeElement.closest(".wz-pane")) h1.current?.focus({ preventScroll: true }); }, [step]);
  return (
    <>
      <section className={`wz-card wz-pane${className ? " " + className : ""}`} aria-labelledby="wz-title">
        <header className="wz-pane-head">
          <Brand />
          {i >= 0 ? <span className="wz-eyebrow tabular">Step {i + 1} of {flow.length}</span> : head}
        </header>
        {at && <Stepper at={at} />}
        <div className="wz-pane-body">
          <div className="wz-pane-inner" key={step} onKeyDown={arrowKeys}>
            <h1 id="wz-title" ref={h1} tabIndex={-1}>{title}</h1>
            {(sub || more) && <p className="wz-sub">{sub}{sub && more && " "}{more && <AcademyLink to={more[0]} about={more[1]}>Learn more</AcademyLink>}</p>}
            <Note />
            {children}
          </div>
        </div>
        {(back || primary || footLeft) && (
          <footer className="wz-pane-foot">
            {back ? <button type="button" className="btn btn-lg btn-ghost wz-back" onClick={back}><ArrowLeft size={16} aria-hidden="true" />Back</button> : (footLeft ?? <span />)}
            {primary && <div className="wz-foot-right"><span className="wz-hint" aria-hidden="true"><kbd>↵</kbd>to continue</span>{primary}</div>}
          </footer>
        )}
      </section>
      {!centered && (
        <aside className="wz-stage" aria-label="Preview">
          <span className="wz-pill glass-pill"><span className="status-dot s-done" />Demo island · no Minecraft files needed</span>
          {stage}
        </aside>
      )}
    </>
  );
}

function DemoLink({ lead = "No world at hand?" }: { lead?: string }) {
  return (
    <button type="button" className="wz-demo" onClick={() => void openDemo()}>
      {lead && <span className="muted">{lead} </span>}<span className="accent">Try the demo island</span>
    </button>
  );
}

// ---------------- welcome (first launch) ----------------

function Welcome() {
  const [joining, setJoining] = useState(false);
  useStepKeys({ enter: afterWelcome });
  return (
    <>
      <div className="wz-scrim" aria-hidden="true" />
      <header className="wz-top">
        <Brand by />
        <TopNav />
      </header>
      <main className="wz-card wz-hero-copy" aria-labelledby="wz-title">
        <div className="wz-overline"><span aria-hidden="true" />Minecraft world planner</div>
        <h1 id="wz-title">Plan your world before you build it.</h1>
        <p className="wz-lede">Paths, towns and builds in 3D, on your own Minecraft world. Free, and it all stays on your computer.</p>
        <div className="wz-hero-actions">
          <button type="button" className="btn btn-xl btn-primary" onClick={afterWelcome}>Get started<ArrowRight size={16} aria-hidden="true" /></button>
          <button type="button" className="btn btn-xl btn-outline wz-demo" onClick={() => void openDemo()}>Try the demo island</button>
        </div>
        {joining
          ? <div className="wz-hero-join"><JoinByLink disabled={false} autoFocus /></div>
          : <button type="button" className="wz-textlink" onClick={() => setJoining(true)}><Link2 size={16} aria-hidden="true" />I have an invite link</button>}
        <ul className="wz-facts" aria-label="Good to know">
          <li><ShieldCheck size={16} aria-hidden="true" /><b>Stays on your computer</b><span>No account to plan alone</span></li>
          <li><Package size={16} aria-hidden="true" /><b>Your real blocks</b><span>Read from your own game</span></li>
          <li><Puzzle size={16} aria-hidden="true" /><b>Modded worlds too</b><span>Fabric, Vanilla+ and more</span></li>
        </ul>
      </main>
      <footer className="wz-foot">
        <div className="legal-footer">
          <p>Not an official Minecraft product. Not approved by or associated with Mojang or Microsoft.</p>
          <nav aria-label="Legal">
            <LegalLink path="legal/privacy.html">Privacy</LegalLink>
            <LegalLink path="legal/terms.html">Terms</LegalLink>
            <LegalLink path="legal/index.html">Legal notice</LegalLink>
          </nav>
        </div>
      </footer>
    </>
  );
}

// ---------------- step 1: where you play ----------------

const LAUNCHER_ICON: Record<Launcher, typeof Box> = { official: Box, modrinth: Wrench, curseforge: Flame, prism: Triangle, other: Folder };

function LauncherStep() {
  const launcher = useOnboarding((s) => s.launcher);
  const os = useOnboarding((s) => s.os);
  const pick = (l: Launcher) => { ob().set({ launcher: l }); go("textures"); };
  const back = () => go("welcome");
  useStepKeys({ enter: () => { if (launcher) go("textures"); }, back, digit: (n) => { const l = LAUNCHERS[n - 1]; if (l) pick(l.id); } });
  return (
    <Split
      centered
      at="launcher"
      title="Where do you play Minecraft?"
      sub="So MC Planner can show you exactly where your game keeps its files. You answer this once."
      more={["findTheJar", "finding the game jar"]}
      back={back}
      primary={<button type="button" className="btn btn-lg btn-primary" disabled={!launcher} onClick={() => go("textures")}>Continue</button>}
    >
      <div className="wz-tiles" role="list">
        {LAUNCHERS.map((l, i) => {
          const Icon = LAUNCHER_ICON[l.id];
          const on = launcher === l.id;
          return (
            <button
              key={l.id}
              type="button"
              role="listitem"
              className={`wz-tile${on ? " selected" : ""}`}
              aria-current={on ? "true" : undefined}
              onClick={() => pick(l.id)}
            >
              <span className="wz-tile-icon"><Icon size={16} aria-hidden="true" /></span>
              <span className="wz-tile-text"><b>{l.name}</b><span>{l.hint.replace(" & ", " and ")}</span></span>
              {on ? <span className="wz-tick" aria-label="Selected"><Check size={13} strokeWidth={3} aria-hidden="true" /></span> : <kbd>{i + 1}</kbd>}
            </button>
          );
        })}
      </div>
      <OsSwitch os={os} />
    </Split>
  );
}

function OsSwitch({ os }: { os: OS }) {
  return (
    <div className="wz-os">
      <span>Paths for{os !== detectOS() && <span className="muted"> (not this computer)</span>}</span>
      <div className="seg" role="group" aria-label="Your system">
        {(["windows", "mac", "linux"] as OS[]).map((o) => <button key={o} type="button" aria-pressed={o === os} onClick={() => ob().set({ os: o })}>{OS_NAME[o]}</button>)}
      </div>
    </div>
  );
}

/** the folders of a launcher as a small tree: the base folder, then the jar's and the worlds' place inside it */
function launcherTree(l: Launcher, os: OS) {
  const sep = os === "windows" ? "\\" : "/";
  const jar = jarHelp(l, os).folder.split(sep), saves = savesHelp(l, os).folder.split(sep);
  let i = 0;
  while (i < jar.length - 1 && i < saves.length - 1 && jar[i] === saves[i]) i++;
  const root = jar.slice(0, i).join(sep);
  const jarRel = jar.slice(i).join(sep);
  const savesRel = saves.slice(i).join(sep);
  const v = "26.2";
  const jarFile = l === "prism" ? `${sep}${v}${sep}minecraft-${v}-client.jar` : `${sep}${v}${sep}${v}.jar`;
  const savesTail = l === "official" || l === "other" ? sep : l === "prism" ? `${sep}‹instance›${sep}.minecraft${sep}saves${sep}` : `${sep}‹${l === "modrinth" ? "profile" : "instance"}›${sep}saves${sep}`;
  return { root, jar: jarRel + jarFile, saves: savesRel + savesTail };
}

function TreePath({ text }: { text: string }) {
  // ‹placeholders› in a quieter ink
  return <>{text.split(/(‹[^›]+›)/).map((p, i) => (p.startsWith("‹") ? <span key={i} className="ph">{p.slice(1, -1)}</span> : p))}</>;
}

// ---------------- step 2: your blocks ----------------

function TexturesStep({ found }: { found: FoundMinecraft | null }) {
  const assetsLabel = useApp((s) => s.assetsLabel);
  const mapColoursOnly = useApp((s) => s.mapColoursOnly);
  const progress = useApp((s) => s.progress);
  const launcher = useOnboarding((s) => s.launcher);
  const os = useOnboarding((s) => s.os);
  const extras = useOnboarding((s) => s.extras);
  const pending = useOnboarding((s) => s.pendingWorld);
  const note = useOnboarding((s) => s.note);
  const [arrived, setArrived] = useState(false);
  const [extrasOpen, setExtrasOpen] = useState(false);
  const busy = !!progress;
  const desktop = isDesktop();
  const ready = !!assetsLabel && !mapColoursOnly;
  // the jar was read while this step was on screen (→ the mosaic moment, then on by itself)
  const sawBusy = useRef(false);
  if (busy) sawBusy.current = true;

  const advanced = useRef(false);
  const advance = () => {
    if (advanced.current) return;
    advanced.current = true;
    const pw = ob().pendingWorld;
    go(isNewUser() ? "world" : "home");
    if (pw) void startWorld(() => (pw.zip ? controller.openZip(pw.zip) : controller.openFolderFiles(pw.files ?? [], pw.name)), { name: pw.name.replace(/\.zip$/i, "") });
  };

  // textures arrived here: show them off for a moment, then on to the world (or open the world dropped earlier)
  const handled = useRef(false);
  useEffect(() => {
    if (!ready || handled.current) return;
    handled.current = true;
    const wasHere = sawBusy.current;
    if (wasHere) setArrived(true);
    // came back to this step with the jar already read: stay (Change / Continue)
    if (!wasHere && !ob().pendingWorld) return;
    const t = setTimeout(advance, wasHere && !prefersReducedMotion() ? 2200 : 0);
    return () => clearTimeout(t);
  }, [ready]);

  // desktop, new user, exactly one game jar found: just use it
  const auto = useRef(false);
  useEffect(() => {
    if (!desktop || auto.current || ready || busy || !found || found.jars.length !== 1 || !isNewUser()) return;
    auto.current = true;
    void controller.loadLocalJar(found.jars[0].path, found.jars[0].version);
  }, [found, ready, busy]);

  const help = jarHelp(launcher ?? "official", os);
  const [s1, s2] = openFolderSteps(os);
  const back = () => go(isNewUser() ? (desktop ? "welcome" : "launcher") : "home");
  const firstJar = desktop ? found?.jars[0] : undefined;
  const next = () => {
    if (ready) advance();
    else if (firstJar && !busy) void controller.loadLocalJar(firstJar.path, firstJar.version);
  };
  useStepKeys({ enter: next, back });
  const count = ready ? controller.blockCount() : 0;
  const jarName = assetsLabel ? (/\.jar$/i.test(assetsLabel) ? assetsLabel : `${assetsLabel}.jar`) : "";

  return (
    <Split
      centered
      at="textures"
      title="Bring your blocks"
      sub="We can't share Minecraft's game file, so MC Planner reads the block textures from your own copy, right here. Nothing is uploaded."
      more={["bringYourBlocks", "game textures"]}
      back={back}
      primary={<button type="button" className="btn btn-lg btn-primary" disabled={busy || (!ready && !firstJar)} onClick={next}>Continue</button>}
    >
      {pending && !note && <p className="wz-note info"><Info size={16} aria-hidden="true" /><span>World waiting: <b>{pending.name}</b>. It opens as soon as the textures are read.</span></p>}
      {!ready && !busy && <WhyYourGame />}
      {desktop && found && found.jars.length > 0 && !ready && (
        <div className="wz-jars">
          <span className="field-label">Found on this computer</span>
          {found.jars.slice(0, 5).map((j, i) => (
            <button key={j.path} type="button" className={`recent-item wz-row${i === 0 ? " first" : ""}`} disabled={busy} onClick={() => void controller.loadLocalJar(j.path, j.version)}>
              <Box size={16} aria-hidden="true" />
              <span className="grow"><b>Minecraft {j.version}</b><span className="caption">{j.label}</span></span>
              {i === 0 && <span className="chip chip-accent">Newest</span>}
            </button>
          ))}
        </div>
      )}
      {!desktop && !ready && (
        <PathField
          id="wz-jar-path"
          label="Your game jar"
          path={help.folder}
          hint={<>{s1}, then {s2.charAt(0).toLowerCase() + s2.slice(1)}. {help.find} Drag it onto this window.</>}
        />
      )}
      {ready ? (
        <div className="wz-drop-ok" role="status">
          <span className="wz-ok-badge"><Check size={20} strokeWidth={2.5} aria-hidden="true" /></span>
          <span className="grow">
            <b>Your blocks are here</b>
            <span><span className="mono ok">{jarName}</span> · {fmtCount(count)} blocks ready · {desktop ? "read from your launcher" : "kept in this browser"}</span>
          </span>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => { advanced.current = false; handled.current = false; sawBusy.current = false; setArrived(false); void controller.forgetAssets(); }}>Change</button>
        </div>
      ) : (
        <DropZone accept=".jar,.zip" multiple big disabled={busy} label="Choose the Minecraft game jar" className="wz-drop" onFiles={(f) => void routeFiles(f)} onDrop={(dt) => void routeFiles([], dt)}>
          {busy ? (
            <span className="wz-drop-busy" aria-live="polite">
              <LoaderCircle size={20} className="spin" aria-hidden="true" />
              <span className="grow"><b>{progress?.msg ?? "Reading…"}</b><span className="meter"><i style={{ width: `${Math.round((progress?.frac ?? 0) * 100)}%` }} /></span></span>
            </span>
          ) : (
            <>
              <span className="wz-drop-icon"><Upload size={18} aria-hidden="true" /></span>
              <span className="grow">
                <b>{desktop && found?.jars.length ? "Or drop another game .jar here" : "Drop the game .jar here"}</b>
                <span>or click to choose · e.g. <span className="mono">26.2.jar</span></span>
              </span>
            </>
          )}
        </DropZone>
      )}
      {ready && arrived && <BlockStrip />}
      {!ready && (
        <>
          <button type="button" className="wz-row wz-disclose-row" aria-expanded={extrasOpen} onClick={() => setExtrasOpen(!extrasOpen)}>
            <ChevronRight size={16} aria-hidden="true" className="chev" />
            <span className="grow">Resource packs and mod jars</span>
            <span className="caption">{extras.length ? `${extras.length} added · ` : ""}Optional</span>
          </button>
          {extrasOpen && (
            <div className="wz-extras">
              <p className="caption">Add them before the game jar; they're layered on top (first = highest priority). Mod jars add textures for modded blocks. <AcademyLink to="packsAndMods" about="resource packs and mods">Learn more</AcademyLink></p>
              <DropZone accept=".jar,.zip" multiple disabled={busy} className="wz-drop small" onFiles={(f) => ob().set({ extras: [...ob().extras, ...f] })}>
                <span className="grow">{extras.length ? extras.map((f) => f.name).join(", ") : "Drop packs or mod jars here, or click to choose"}</span>
              </DropZone>
            </div>
          )}
          {desktop && <p className="caption wz-tip">Don't see the version you play? Start it once in your launcher, then come back.</p>}
          <button type="button" className="wz-textlink wz-skip" onClick={() => go("world")}>Continue without textures <span className="muted">· demo island &amp; shared worlds</span></button>
        </>
      )}
    </Split>
  );
}

/**
 * Before the game file is there: why MC Planner asks for it at all (the line above it says the reason). Mojang's files
 * may not be handed out (CLAUDE.md: never uploaded, shared or served), so every person brings their own copy and it's
 * only read, on their computer.
 */
function WhyYourGame() {
  const desktop = isDesktop();
  return (
    <section className="wz-why" aria-label="Why we ask for your game">
      <div className="wz-why-flow" aria-hidden="true">
        <span className="wz-why-node">
          <span className="wz-why-ico"><Box size={20} strokeWidth={1.8} /></span>
          <b>Your Minecraft</b>
          <small>{desktop ? "from your launcher" : "the game's .jar"}</small>
        </span>
        <span className="wz-why-link"><i /><span className="wz-why-lock"><Lock size={11} strokeWidth={2.4} />on this computer</span><i /></span>
        <span className="wz-why-node">
          <span className="wz-why-ico app"><BrandMark size={24} /></span>
          <b>MC Planner</b>
          <small>draws your world</small>
        </span>
      </div>
      <ul className="wz-why-list">
        <li>
          <Lock size={16} aria-hidden="true" />
          <span><b>Stays on your computer.</b> {desktop ? "Read from your launcher's folder" : "Read right here and kept in this browser for next time"}. Never uploaded, not even to us.</span>
        </li>
        <li>
          <ShieldCheck size={16} aria-hidden="true" />
          <span><b>Only read.</b> Nothing in your game or your worlds changes.</span>
        </li>
        <li>
          <Users size={16} aria-hidden="true" />
          <span><b>Friends bring their own.</b> When you plan together, everyone uses their own copy. Yours is never passed on.</span>
        </li>
      </ul>
    </section>
  );
}

// ---------------- step 3 / Home: your world ----------------

interface WorldPick {
  key: string;
  name: string;
  icon?: string | null;
  facts: [string, string][];
  profile?: string;
  open: () => Promise<unknown>;
}

const upper = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const baseName = (p: string) => p.split(/[\\/]/).filter(Boolean).pop() ?? p;

function WorldStep({ home, found, onRescan }: { home: boolean; found: FoundMinecraft | null; onRescan: () => void }) {
  const assetsLabel = useApp((s) => s.assetsLabel);
  const mapColoursOnly = useApp((s) => s.mapColoursOnly);
  const progress = useApp((s) => s.progress);
  const phase = useApp((s) => s.phase);
  const launcher = useOnboarding((s) => s.launcher);
  const os = useOnboarding((s) => s.os);
  const last = useOnboarding((s) => s.lastWorld);
  const [recent, setRecent] = useState<RecentWorld[]>([]);
  const [whereOpen, setWhereOpen] = useState(false);
  const [sel, setSel] = useState<string | null>(null);
  const folder = useRef<HTMLInputElement>(null);
  const desktop = isDesktop();
  const busy = !!progress;
  const ready = !!assetsLabel && !mapColoursOnly;
  const locked = !ready || busy;
  useEffect(() => { void controller.recentWorlds().then(setRecent); }, [phase]);
  // pictures: a recent .zip world's own icon.png (read from the zip kept in this browser), the demo island from above
  const [zipIcons, setZipIcons] = useState<Record<string, string | null>>({});
  useEffect(() => {
    let alive = true;
    for (const r of recent) void controller.recentWorldIcon(r.key).then((u) => alive && setZipIcons((m) => (m[r.key] === u ? m : { ...m, [r.key]: u })));
    return () => { alive = false; };
  }, [recent]);
  const [demoIcon, setDemoIcon] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    void demoIslandPicture((n) => controller.demoTopView(n)).then((u) => alive && setDemoIcon(u));
    return () => { alive = false; };
  }, []);

  const fromDesktop = (w: FoundWorld): WorldPick => ({
    key: w.path, name: w.name, icon: w.icon ? fileUrl(w.icon) : null, profile: w.instance,
    facts: [["Profile", w.instance], ["Last played", upper(fmtDate(w.modified))], ["Folder", baseName(w.path)]],
    open: () => controller.openLocalWorld(w.path),
  });
  const fromRecent = (r: RecentWorld): WorldPick => ({
    key: r.key, name: r.name, icon: zipIcons[r.key] ?? null,
    facts: [["File", r.fileName], ["Size", fmtSize(r.size)], ["Added", new Date(r.savedAt).toLocaleDateString()]],
    open: () => controller.openRecent(r),
  });
  const demoPick: WorldPick = { key: "demo", name: DEMO_NAME, icon: demoIcon, facts: [["Blocks", ready ? "Your textures" : "Built in"], ["Files", "None needed"], ["Plan", "Kept in this browser"]], open: () => controller.openDemo() };

  // "Continue": the last world, if it's still there
  const cont: WorldPick | null = !last ? null
    : last.kind === "demo" ? demoPick
    : last.kind === "recent" ? (() => { const r = recent.find((x) => x.key === last.key); return r ? fromRecent(r) : null; })()
    : desktop && found?.worlds.some((w) => w.path === last.path) ? fromDesktop(found.worlds.find((w) => w.path === last.path)!)
    : null;
  const showCont = home && !!cont;
  const picks = new Map<string, WorldPick>();
  if (showCont) picks.set(cont!.key, cont!);
  for (const w of found?.worlds ?? []) if (!picks.has(w.path)) picks.set(w.path, fromDesktop(w));
  for (const r of recent) if (!picks.has(r.key)) picks.set(r.key, fromRecent(r));
  const first = picks.values().next().value as WorldPick | undefined;
  // what the preview shows: the hovered / focused world, else the continue world (Home) or the first in the list
  const picked = (sel && picks.get(sel.replace(/^cont:/, ""))) || first || null;
  const contSelected = showCont && (!sel || sel.startsWith("cont:"));
  const rowSelected = (key: string) => sel === key || (!sel && !showCont && first?.key === key);
  const openPick = (p: WorldPick) => void startWorld(p.open, { name: p.name, profile: p.profile });

  const back = home ? undefined : () => go("textures");
  const chooseFolder = () => folder.current?.click();
  const primaryAction = () => { if (locked) return; if (picked) openPick(picked); else chooseFolder(); };
  useStepKeys({ enter: primaryAction, back });

  const saves = savesHelp(launcher ?? "official", os);
  const launcherName = LAUNCHERS.find((l) => l.id === launcher)?.name;
  const sub = home
    ? (showCont ? "Pick up where you left off, or open another world." : "Pick a world. MC Planner only reads it and never changes your save.")
    : desktop
      ? "Worlds from your launchers, newest first. Or drop a world folder or .zip anywhere on this window."
      : `Drop your world's folder or a .zip of it anywhere on this window${launcherName && launcher !== "other" ? ` (it's in ${launcherName}'s saves)` : ""}. MC Planner only reads it and never changes your save.`;

  const dropRow = <DropRow disabled={locked} roomy={!desktop && !recent.length} onFiles={(f) => void routeFiles(f)} onDrop={(dt) => void routeFiles([], dt)} onFolder={chooseFolder} />;

  const texturesLine = ready
    ? <span className="wz-have"><span className="ok"><Check size={14} strokeWidth={3} aria-hidden="true" />{assetsLabel}</span><button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => { void controller.forgetAssets(); go("textures"); }}>Change textures</button></span>
    : busy ? <span className="wz-have muted"><LoaderCircle size={14} className="spin" aria-hidden="true" />Getting your textures ready…</span>
    : <span className="wz-have"><span className="muted">No game textures yet</span><button type="button" className="btn btn-sm btn-ghost" onClick={() => go("textures")}>Add them</button></span>;

  return (
    <Split
      at={home ? undefined : "world"}
      head={<TopNav />}
      title={showCont ? "Welcome back" : "Which world are we planning?"}
      sub={sub}
      more={["openYourWorld", "opening your world"]}
      back={back}
      footLeft={home ? texturesLine : undefined}
      primary={home ? undefined : (
        <button type="button" className="btn btn-lg btn-primary wz-open" disabled={locked} onClick={primaryAction}>
          {picked ? <>Open {picked.name}<ArrowRight size={16} aria-hidden="true" /></> : <>Choose the world folder</>}
        </button>
      )}
      stage={<WorldPreview pick={picked} os={os} launcher={launcher} desktop={desktop} />}
    >
      {!home && !ready && !busy && (
        <p className="wz-note info"><Info size={16} aria-hidden="true" /><span>Without the game's textures you can open the demo island (in MC Planner's own blocks) or a world shared with you (in map colours). <button type="button" className="link" onClick={() => go("textures")}>Add textures</button> to open your own worlds.</span></p>
      )}
      {!home && busy && <p className="wz-quiet"><LoaderCircle size={14} className="spin" aria-hidden="true" /> Getting your textures ready…</p>}
      {showCont && (
        <button type="button" className={`wz-continue${contSelected ? " selected" : ""}`} disabled={locked} onClick={() => openPick(cont!)} onFocus={() => setSel("cont:" + cont!.key)} onMouseEnter={() => setSel("cont:" + cont!.key)}>
          {cont!.icon ? <img src={cont!.icon} alt="" width={46} height={46} /> : <WorldTile name={cont!.name} size={46} />}
          <span className="grow"><span className="overline">Continue planning</span><b>{cont!.name}</b><span className="caption">{cont!.facts.map((f) => f[1]).slice(0, 2).join(" · ")}</span></span>
          <ArrowRight className="arrow" size={18} aria-hidden="true" />
        </button>
      )}
      {showCont && (found?.worlds.length || recent.length) ? <div className="wz-or">or open another world</div> : null}
      {desktop && found && (
        <DesktopWorlds
          found={found}
          disabled={locked}
          onRescan={onRescan}
          selected={(k) => rowSelected(k)}
          onSelect={(w) => setSel(w.path)}
          focusFirst={false}
          onOpen={(w) => openPick(fromDesktop(w))}
        />
      )}
      {recent.length > 0 && (
        <div className="recent">
          <span className="field-label">{desktop ? "Recent .zip worlds" : "Recent worlds · kept in this browser"}</span>
          <div className="world-grid" role="listbox" aria-label="Recent worlds">
            {recent.map((r, i) => (
              <WorldRow
                key={r.key}
                className="recent-item"
                icon={zipIcons[r.key]}
                name={r.name}
                sub={`${r.fileName} · ${fmtSize(r.size)}`}
                when={new Date(r.savedAt).toLocaleDateString()}
                selected={rowSelected(r.key)}
                disabled={locked}
                onOpen={() => openPick(fromRecent(r))}
                onSelect={() => setSel(r.key)}
              />
            ))}
          </div>
        </div>
      )}
      {dropRow}
      <input ref={folder} type="file" hidden {...({ webkitdirectory: "", directory: "" } as object)} onChange={(e) => { if (e.target.files?.length) { const files = e.target.files; const name = (files[0] as File & { webkitRelativePath?: string }).webkitRelativePath?.split("/")[0] || "your world"; void startWorld(() => controller.openFolder(files), { name }); } e.target.value = ""; }} />
      {desktop && <JoinByLink disabled={busy} />}
      <SharedWorldsList disabled={busy} />
      {!desktop && (
        <>
          <button type="button" className="wz-row wz-disclose-row" aria-expanded={whereOpen} onClick={() => setWhereOpen(!whereOpen)}>
            <ChevronRight size={16} aria-hidden="true" className="chev" />
            <span className="grow">Where are my worlds?</span>
            <span className="caption">{OS_NAME[os]}</span>
          </button>
          {whereOpen && (
            <div className="wz-extras">
              <PathField id="wz-saves-path" label={launcherName && launcher !== "other" ? `${launcherName} saves` : "Saves folder"} path={saves.folder} hint={<>{saves.find} Zipping instead? {zipHelp(os)} <AcademyLink to="whereAreWorlds" about="where worlds are saved">Learn more</AcademyLink></>} />
            </div>
          )}
        </>
      )}
      <DemoLink />
    </Split>
  );
}

/**
 * The world step's frame: the hovered / focused world's own picture (icon.png from its folder or zip, the demo island
 * from above), big and pixel-sharp; a world without one shows its pixel map (lib/worldThumb.ts). No worlds yet: where
 * the launcher keeps them.
 */
function WorldPreview({ pick, os, launcher, desktop }: { pick: WorldPick | null; os: OS; launcher: Launcher | null; desktop: boolean }) {
  if (!pick) {
    const t = launcherTree(launcher ?? "official", os);
    return (
      <div className="wz-thumb-stage">
        <div className="wz-glass wz-tree-card">
          <div className="wz-card-head"><span className="overline">Your worlds are here</span><span className="caption">{OS_NAME[os]}</span></div>
          <div className="wz-tree mono">
            <div className="root">{t.root}</div>
            <div><span><span className="tw">└─ </span><TreePath text={t.saves} /></span><em>One folder per world</em></div>
          </div>
          <p className="caption">{desktop ? "No worlds found yet. Add a folder or drop one on this window." : "Drop the world's folder (or a .zip of it) anywhere on this window."}</p>
        </div>
      </div>
    );
  }
  const own = !!pick.icon;
  return (
    <div className="wz-thumb-stage">
      <div className="wz-thumb-wrap" key={pick.key}>
        <figure className="wz-thumb">
          <img src={pick.icon ?? namePicture(pick.name)} alt={own ? `${pick.name}, as Minecraft last saw it` : ""} draggable={false} />
        </figure>
        <div className="wz-thumb-info">
          {!own && <span className="wz-thumb-note">No picture of its own yet</span>}
          <span className="wz-thumb-name">{pick.name}</span>
          <span className="caption-2">{pick.key === "demo" ? "A generated island to try things on. Nothing to download." : <>Opens at the world's home point or spawn. <kbd>H</kbd> always flies you home.</>}</span>
          <dl>
            {pick.facts.map(([k, v]) => <div key={k}><dt>{k}</dt><dd title={v}>{v}</dd></div>)}
          </dl>
        </div>
      </div>
    </div>
  );
}

// ---------------- opening a world ----------------

/**
 * A new user stays on the loading screen until the world is drawn (map colours + the 3D blocks around the landing
 * spot), then goes in by themselves. A big world or a slow computer doesn't keep them waiting past this, counted from
 * the moment the world is open (the rest keeps loading on the map); "Go in now" (Enter) goes in at once.
 */
const HOLD_MAX_MS = 15_000;
const goIn = () => ob().set({ holding: false });

/**
 * How many 3D chunks the view around the landing spot asks for: the same circle the engine streams
 * (render distance around the camera target, only chunks that exist in the world). Read from the loaded region maps.
 */
function useChunkGoal(active: boolean) {
  const [goal, setGoal] = useState(0);
  useEffect(() => {
    if (!active) return;
    const read = () => {
      const e = controller.engine;
      if (!e) return;
      const t = e.controls.target;
      const camDist = e.camera.position.distanceTo(t);
      const rd = useApp.getState().renderDistance;
      const R = camDist > 1400 ? 0 : Math.max(0, Math.round(rd * (camDist > 700 ? 0.5 : 1)));
      const presence = new Map(e.exportRegionMaps().map((m) => [`${m.rx},${m.rz}`, m.presence] as const));
      const tcx = Math.floor(t.x / 16), tcz = Math.floor(t.z / 16);
      let n = 0;
      for (let dz = -R; dz <= R; dz++)
        for (let dx = -R; dx <= R; dx++) {
          if (Math.hypot(dx, dz) > R + 0.5) continue;
          const cx = tcx + dx, cz = tcz + dz;
          const p = presence.get(`${cx >> 5},${cz >> 5}`);
          if (p && p[(cz & 31) * 32 + (cx & 31)]) n++;
        }
      setGoal(n);
    };
    read();
    const iv = setInterval(read, 700);
    return () => clearInterval(iv);
  }, [active]);
  return goal;
}

type Chapter = { id: string; title: string; sub: string; state: "done" | "now" | "todo" | "off"; frac: number };

function Opening({ hold }: { hold: boolean }) {
  const phase = useApp((s) => s.phase);
  const progress = useApp((s) => s.progress);
  const stats = useApp((s) => s.stats);
  const level = useApp((s) => s.level);
  const demo = useApp((s) => s.demo);
  const assetsLabel = useApp((s) => s.assetsLabel);
  const mapColoursOnly = useApp((s) => s.mapColoursOnly);
  const invite = useApp((s) => s.invite);
  const opening = useOnboarding((s) => s.opening);
  const leaving = useOnboarding((s) => s.leaving);
  const from = useOnboarding((s) => s.step);
  const ready = phase === "ready";
  // the generated demo island has 3D blocks too: the game's textures when loaded, else MC Planner's built-in ones
  const builtIn = demo && level?.version === "demo" && !(assetsLabel && !mapColoursOnly);
  const noBlocks = mapColoursOnly || (demo && level?.version !== "demo");
  const chunkGoal = useChunkGoal(ready && !noBlocks);

  // 3D blocks count as done once nothing is in flight for a moment (some wanted chunks are empty)
  const [settled, setSettled] = useState(false);
  const busyChunks = (stats?.pending ?? 0) > 0;
  useEffect(() => {
    setSettled(false);
    if (!ready || noBlocks || busyChunks || !(stats?.chunks)) return;
    const t = setTimeout(() => setSettled(true), 1500);
    return () => clearTimeout(t);
  }, [ready, noBlocks, busyChunks, !!stats?.chunks]);

  // Enter goes in (once the world is open), Esc goes back — before the map's own shortcuts see the key
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey || (e.target as HTMLElement)?.closest?.("input, textarea, select, button")) return;
      if (e.key === "Enter" && hold && ready && !leaving) { e.preventDefault(); e.stopPropagation(); goIn(); }
      else if (e.key === "Escape" && !leaving) { e.preventDefault(); e.stopPropagation(); backToWorlds(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [hold, ready, leaving]);

  const name = level?.name ?? opening?.name ?? invite?.manifest?.name ?? "your world";
  const total = stats?.regionsTotal ?? 0, loaded = Math.min(stats?.regionsLoaded ?? 0, total);
  const mapsFrac = ready ? (total ? loaded / total : 0) : 0;
  const chunks = stats?.chunks ?? 0;
  const chunkTotal = Math.max(chunkGoal, chunks);
  const chunksFrac = settled ? 1 : chunkTotal ? Math.min(1, chunks / chunkTotal) : 0;
  const worldFrac = ready ? 1 : Math.min(0.95, progress?.frac ?? 0.1);
  const texturesDone = !!assetsLabel || mapColoursOnly || builtIn;

  const chapters: Chapter[] = [
    { id: "world", title: "World", sub: ready ? "Level and spawn point" : "Reading level.dat…", state: ready ? "done" : "now", frac: worldFrac },
    {
      id: "textures", title: "Block textures",
      sub: noBlocks ? "Map colours only" : builtIn ? "MC Planner's built-in blocks" : assetsLabel ? `${fmtCount(controller.blockCount())} from ${/\.jar$/i.test(assetsLabel) ? assetsLabel : assetsLabel + ".jar"}` : "Waiting for the game jar",
      state: noBlocks ? "off" : texturesDone ? "done" : "todo", frac: texturesDone ? 1 : 0,
    },
    {
      id: "maps", title: "Map colours",
      sub: ready && total ? `${loaded} of ${total} region${total === 1 ? "" : "s"}` : "Regions of the map",
      state: !ready ? "todo" : total && loaded >= total ? "done" : "now", frac: mapsFrac,
    },
    {
      id: "chunks", title: "3D blocks around you",
      sub: noBlocks ? "Off · plan on the map colours" : ready && chunkTotal ? `${fmtCount(chunks)} of ${fmtCount(chunkTotal)} chunks${hold ? " · then you're in" : ""}` : "Chunks around the landing spot",
      state: noBlocks ? "off" : !ready ? "todo" : chunksFrac >= 1 ? "done" : "now", frac: chunksFrac,
    },
  ];
  const pct = Math.round(100 * (noBlocks ? worldFrac * 0.2 + mapsFrac * 0.8 : worldFrac * 0.15 + (texturesDone ? 0.05 : 0) + mapsFrac * 0.4 + chunksFrac * 0.4));
  const allDone = chapters.every((c) => c.state === "done" || c.state === "off");
  const activity = leaving ? "Going back…" : !ready ? "Reading your world…" : allDone ? "Ready" : chapters[2].state === "now" ? "Drawing the map…" : "Building the 3D blocks…";

  // in by themselves: a moment after it's all drawn, or HOLD_MAX_MS after the world opened
  const readyAt = useRef(0);
  if (ready && !readyAt.current) readyAt.current = performance.now();
  useEffect(() => {
    if (!hold || !ready || leaving) return;
    const t = setTimeout(goIn, allDone ? 700 : Math.max(0, readyAt.current + HOLD_MAX_MS - performance.now()));
    return () => clearTimeout(t);
  }, [hold, ready, allDone, leaving]);
  // screen readers hear the chapters, not every percent
  const milestone = leaving ? "Going back to your worlds" : allDone ? `${name} is ready` : !ready ? `Opening ${name}` : chapters[2].state === "now" ? "Drawing the map" : "Building the 3D blocks";

  // facts from the world itself: profile · version · mod loader · last played
  const brands = (level?.brands ?? []).filter((b) => b && b.toLowerCase() !== "vanilla").map((b) => upper(b));
  const facts = demo || level?.version === "demo"
    ? ["Demo island", builtIn ? "built-in blocks" : "your textures", "no Minecraft files needed"]
    : [
      opening?.profile,
      level?.version && level.version !== "unknown" ? `Minecraft ${level.version}` : null,
      level?.modded ? (brands.length ? brands.join(" + ") : "modded") : null,
      level?.lastPlayed ? `played ${fmtDate(Number(level.lastPlayed) / 1000)}` : null,
    ].filter(Boolean) as string[];
  const old = !!level && level.dataVersion > 0 && level.dataVersion < 2860;

  const veil = !ready ? 0.82 : total ? 0.82 * (1 - Math.min(1, mapsFrac * 1.6)) : 0.82;
  return (
    <>
      <div className="wz-veil" style={{ opacity: veil }} aria-hidden="true" />
      <div className="wz-vignette" aria-hidden="true" />
      <div className="wz-bottom-scrim" aria-hidden="true" />
      <header className="wz-top">
        <Brand />
        <span className="wz-top-actions">
          {hold && ready && !allDone && !leaving && <button type="button" className="btn wz-go-in" onClick={goIn}>Go in now<kbd>Enter</kbd></button>}
          <button type="button" className="btn wz-esc" onClick={backToWorlds} disabled={leaving}><kbd>Esc</kbd>{leaving ? "Going back…" : from === "welcome" ? "Back" : "Back to worlds"}</button>
        </span>
      </header>
      <section className="wz-open-bottom" aria-labelledby="wz-title">
        <p className="sr-only" aria-live="polite">{milestone}</p>
        <div className="wz-name">
          <span className="overline">Opening</span>
          <h1 id="wz-title" className="wz-world-name">{name}</h1>
          {(facts.length > 0 || old) && (
            <span className="wz-facts-line">
              {facts.join(" · ")}
              {old && <span className="chip chip-amber" title="Minecraft 1.18 changed how worlds are saved. Open and save it once in a newer Minecraft for the full 3D view.">Saved before 1.18 · parts may be missing</span>}
            </span>
          )}
        </div>
        <div className="wz-pct" aria-hidden="true">
          <span className="wz-pct-n tabular"><b>{pct}</b><span>%</span></span>
          <span className="caption-2">{activity}</span>
        </div>
        <ol className="wz-chapters" aria-label="Loading steps">
          {chapters.map((c) => (
            <li key={c.id} className={c.state} aria-current={c.state === "now" ? "step" : undefined}>
              <span className="wz-bar" role="progressbar" aria-label={c.title} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(c.frac * 100)}>
                <i style={{ width: `${c.state === "done" ? 100 : c.state === "off" ? 0 : Math.round(c.frac * 100)}%` }} />
              </span>
              <span className="t">
                {c.state === "done" ? <Check size={13} strokeWidth={2.6} aria-hidden="true" /> : c.state === "now" ? <LoaderCircle size={13} strokeWidth={2.6} className="spin" aria-hidden="true" /> : c.state === "off" ? <Minus size={13} strokeWidth={2.6} aria-hidden="true" /> : <span className="dot" aria-hidden="true" />}
                {c.title}
              </span>
              <span className="s tabular">{c.sub}</span>
            </li>
          ))}
        </ol>
      </section>
    </>
  );
}

// ---------------- guests ----------------

/**
 * Guest without Minecraft on this computer: one click and this browser downloads the host's version of the game file
 * straight from Mojang (lib/mojang.ts) — nothing from Mojang ever passes through MC Planner's server. After the
 * first "yes" in this browser it starts by itself.
 */
function MojangOffer({ version, busy }: { version: string; busy: boolean }) {
  const [client, setClient] = useState<MojangClient | null>(null);
  const [unreachable, setUnreachable] = useState(false);
  useEffect(() => {
    let live = true;
    findClient(version).then((c) => live && setClient(c), () => live && setUnreachable(true));
    return () => { live = false; };
  }, [version]);
  // said yes before in this browser: no click needed (unless a cached jar is being restored right now)
  const auto = useRef(false);
  useEffect(() => {
    if (auto.current || !client || busy || !mojangAccepted()) return;
    auto.current = true;
    const t = setTimeout(() => {
      const s = useApp.getState();
      if (!s.progress && !(s.assetsLabel && !s.mapColoursOnly)) void controller.loadMojangTextures(version);
    }, 800);
    return () => clearTimeout(t);
  }, [client, busy, version]);
  if (unreachable) return null;
  return (
    <div className="wz-mojang">
      <button type="button" className="btn btn-lg btn-outline" disabled={busy || !client} onClick={() => void controller.loadMojangTextures(version)}>
        Get the block textures from Mojang
      </button>
      <span className="caption">
        {client
          ? <>Minecraft {client.id} · {fmtMB(client.size)} · downloaded once, straight from Mojang to this computer{client.exact ? "" : ` (Mojang doesn't list ${version}; this is the closest release)`}.</>
          : "Asking Mojang…"}
      </span>
      <span className="caption">
        By clicking you confirm you own Minecraft: Java Edition and accept the <a href={EULA_URL} target="_blank" rel="noreferrer">Minecraft EULA</a>.
        MC Planner never stores or passes on Mojang's files.
      </span>
    </div>
  );
}

function InviteStep({ found }: { found: FoundMinecraft | null }) {
  const invite = useApp((s) => s.invite)!;
  const assetsLabel = useApp((s) => s.assetsLabel);
  const mapColoursOnly = useApp((s) => s.mapColoursOnly);
  const progress = useApp((s) => s.progress);
  const busy = !!progress;
  const ready = !!assetsLabel && !mapColoursOnly;
  const desktop = isDesktop();
  const m = invite.manifest;

  // desktop guest: use the game jar of this computer right away (the version the host plays, else the newest)
  const autoJar = useRef(false);
  useEffect(() => {
    if (!desktop || !m || ready || busy || autoJar.current || !found?.jars.length) return;
    autoJar.current = true;
    const jar = found.jars.find((j) => j.version === m.version) ?? found.jars[0];
    void controller.loadLocalJar(jar.path, jar.version);
  }, [desktop, m, ready, busy, found]);

  const hasLocalJar = desktop && !!found?.jars.length;
  const textures = ready
    ? <p className="wz-have"><span className="ok"><Check size={14} strokeWidth={3} aria-hidden="true" />{assetsLabel}</span></p>
    : (
      <div className="wz-guest-jar">
        {m && !hasLocalJar && <MojangOffer version={m.version} busy={busy} />}
        <DropZone accept=".jar,.zip" multiple disabled={busy} label="Choose your Minecraft game jar" className="wz-drop small" onFiles={(f) => void routeFiles(f)} onDrop={(dt) => void routeFiles([], dt)}>
          {busy
            ? <span className="wz-drop-busy"><LoaderCircle size={16} className="spin" aria-hidden="true" /><span className="grow"><b>{progress?.msg}</b><span className="meter"><i style={{ width: `${Math.round((progress?.frac ?? 0) * 100)}%` }} /></span></span></span>
            : <><Upload size={16} aria-hidden="true" className="wz-droprow-icon" /><span className="grow"><b>{hasLocalJar ? `Drop your Minecraft ${m?.version ?? ""} .jar here` : "Minecraft installed here? Drop its .jar instead"}</b><span>or click to choose · it's read on this computer only</span></span></>}
        </DropZone>
      </div>
    );
  const size = m ? m.files.reduce((a, f) => a + f.size, 0) : 0;
  return (
    <Split
      className="wz-guest"
      head={<TopNav />}
      title={m?.host ? `${m.host} invited you to plan together` : "You're invited to plan together"}
      sub="Plan on the host's world together, live. You only need the game's block textures."
      more={["inviteRoles", "invites and roles"]}
      stage={m ? (
        <div className="wz-glass wz-world-card">
          <div className="wz-world-main">
            <WorldTile name={m.name} size={52} />
            <span className="grow"><span className="title-md">{m.name}</span><span className="caption-2">{m.host ? `Hosted by ${m.host}` : "A shared world"} · streams from the host</span></span>
          </div>
          <dl>
            <div><dt>Version</dt><dd>{m.version}</dd></div>
            <div><dt>Size</dt><dd>{fmtSize(size)}</dd></div>
            <div><dt>Updated</dt><dd>{new Date(m.updatedAt).toLocaleDateString()}</dd></div>
          </dl>
        </div>
      ) : null}
    >
      <JoinSection assetsReady={ready} busy={busy} textures={textures} />
    </Split>
  );
}

function AccountInvite() {
  return (
    <Split centered className="wz-guest" head={<TopNav />} title="You're invited to plan together" sub="Joining is free: a free account lets the owner see who's who." more={["inviteRoles", "invites and roles"]}>
      <AccountInviteSection />
    </Split>
  );
}
